import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

// SSR-safe browser-global access, used ONLY inside a client effect. Local instead
// of `getWindow` from @ztechapps/common.utils because that barrel is not
// tree-shakeable and drags numeral/moment/libphonenumber into this marketing
// bundle (~2 MB) and trips the rolldown-vite minifier.
type GLightboxWindow = { GLightbox?: (opts: { selector: string }) => { destroy?: () => void } }
const getWindow = (): GLightboxWindow | undefined => (typeof window === 'undefined' ? undefined : (window as unknown as GLightboxWindow))

type GalleryImage = {
  src: string
  alt: string
}

// Install photos are a manual drop-in (see public/assets/img/gallery/README.md).
// Until they exist the tiles 404: each failed image drops out of the grid, and if
// none load the section shows an understated "coming soon" state instead of broken
// image icons — so a missing asset never breaks the page.
const GALLERY_IMAGES: GalleryImage[] = [
  { src: '/assets/img/gallery/install-1.webp', alt: 'Gatekeeper gate hardware installation' },
  { src: '/assets/img/gallery/install-2.webp', alt: 'RFID scanner mounted at a school gate' },
  { src: '/assets/img/gallery/install-3.webp', alt: 'Attendance terminal with monitor' },
  { src: '/assets/img/gallery/install-4.webp', alt: 'SMS sender and networking hardware' },
  { src: '/assets/img/gallery/install-5.webp', alt: 'Movable monitor stand at the entrance' },
  { src: '/assets/img/gallery/install-6.webp', alt: 'Gatekeeper station in daily use' },
]

type GallerySectionProps = {
  children?: ReactNode
}

// #gallery — glightbox photo grid + the "What's included" list (passed as children).
export const GallerySection = ({ children }: GallerySectionProps) => {
  const [failed, setFailed] = useState<Record<number, boolean>>({})

  const markFailed = (index: number) => setFailed((prev) => (prev[index] ? prev : { ...prev, [index]: true }))

  const allFailed = GALLERY_IMAGES.every((_, index) => failed[index])

  // (Re)bind glightbox to whatever gallery anchors currently exist. INSPINIA's
  // landing.js does NOT initialize glightbox, so we do it here — guarded via
  // getWindow() so the SSR render never touches the browser global, and typed as
  // a no-op when the lib has not loaded (nothing throws, the grid just isn't a
  // lightbox). boundKey changes whenever a tile drops out, re-binding to the
  // remaining valid anchors.
  const boundKey = GALLERY_IMAGES.map((_, index) => (failed[index] ? '0' : '1')).join('')
  useEffect(() => {
    const createLightbox = getWindow()?.GLightbox
    if (typeof createLightbox !== 'function') return
    const lightbox = createLightbox({ selector: '.gk-gallery-item' })
    return () => lightbox?.destroy?.()
  }, [boundKey])

  return (
    <section className="section-custom" id="gallery">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center">
            <span className="text-muted rounded-3 d-inline-block">See it in place</span>
            <h2 className="mt-3 fw-bold mb-5">
              Neutral, campus-ready <mark className="fst-italic">hardware</mark>
            </h2>
          </div>
        </div>

        <div className="row g-4 align-items-start">
          <div className="col-lg-7">
            {allFailed ? (
              <div className="border border-dashed rounded-4 p-5 text-center text-muted">
                <i className="ti ti-photo fs-1 d-block mb-2"></i>
                Install photos coming soon.
              </div>
            ) : (
              <div className="row g-3">
                {GALLERY_IMAGES.map((image, index) =>
                  failed[index] ? null : (
                    <div className="col-6" key={image.src}>
                      <a
                        className="gk-gallery-item d-block"
                        href={image.src}
                        data-gallery="gatekeeper-install"
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          loading="lazy"
                          className="img-fluid rounded-3 shadow-sm w-100"
                          onError={() => markFailed(index)}
                        />
                      </a>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          <div className="col-lg-5">{children}</div>
        </div>
      </div>
    </section>
  )
}
